import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { IWizardInstanceConfig, IWizardNavigationState, IWizardStepConfig } from './wizard.types';

@Injectable({
  providedIn: 'root'
})
export class WizardNavigationService {

  private wizardInstances = new Map<string, BehaviorSubject<IWizardNavigationState>>();
  private wizardConfigs = new Map<string, IWizardInstanceConfig>();

  /**
   * Génère un identifiant unique pour une instance de wizard
   */
  generateWizardInstanceId(): string {
    return `wizard_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
  }

  /**
   * Enregistre une nouvelle instance de wizard
   */
  registerWizardInstance(config: IWizardInstanceConfig, steps: IWizardStepConfig[]): void {
    const initialState: IWizardNavigationState = {
      wizardInstanceId: config.wizardInstanceId,
      stepIndex: config.currentStep,
      canGoNext: config.canGoNext,
      canGoPrevious: config.canGoPrevious,
      canComplete: config.canComplete ?? false,
      isCompleteActionInProgress: false,
      isNextActionInProgress: false,
      canSaveAsDraft: true,
      currentStepIndex: config.currentStep,
      totalSteps: config.totalSteps,
      canSkip: config.canSkip ?? true,
      isCompleted: false,
      steps: steps
    };

    this.wizardInstances.set(config.wizardInstanceId, new BehaviorSubject(initialState));
    this.wizardConfigs.set(config.wizardInstanceId, config);
  }

  /**
   * Désenregistre une instance de wizard
   */
  unregisterWizardInstance(wizardInstanceId: string): void {
    const subject = this.wizardInstances.get(wizardInstanceId);
    if (subject) {
      subject.complete();
      this.wizardInstances.delete(wizardInstanceId);
      this.wizardConfigs.delete(wizardInstanceId);
    }
  }

  /**
   * Obtient l'observable pour une instance de wizard spécifique
   */
  getNavigationState$(wizardInstanceId: string): Observable<IWizardNavigationState> | null {
    const subject = this.wizardInstances.get(wizardInstanceId);
    return subject ? subject.asObservable() : null;
  }

  /**
   * Obtient l'état actuel d'une instance de wizard
   */
  getCurrentNavigationState(wizardInstanceId: string): IWizardNavigationState | null {
    const subject = this.wizardInstances.get(wizardInstanceId);
    return subject ? subject.value : null;
  }

  /**
   * Met à jour l'état de navigation pour une instance de wizard spécifique
   */
  updateNavigationState(wizardInstanceId: string, navigationState: Partial<IWizardNavigationState>): void {
    const subject = this.wizardInstances.get(wizardInstanceId);
    if (subject) {
      const currentState = subject.value;
      const newState: IWizardNavigationState = {
        ...currentState,
        ...navigationState
      };
      subject.next(newState);
    }
  }

  /**
   * Met à jour l'état du bouton "Suivant" pour une instance de wizard spécifique
   */
  updateCanGoNext(wizardInstanceId: string, canGoNext: boolean): void {
    const subject = this.wizardInstances.get(wizardInstanceId);
    if (subject) {
      const currentState = subject.value;
      const newState: IWizardNavigationState = {
        ...currentState,
        canGoNext: canGoNext
      };
      subject.next(newState);
    }
  }

  /**
   * Met à jour l'état du bouton "Précédent" pour une instance de wizard spécifique
   */
  updateCanGoPrevious(wizardInstanceId: string, canGoPrevious: boolean): void {
    const subject = this.wizardInstances.get(wizardInstanceId);
    if (subject) {
      const currentState = subject.value;
      const newState: IWizardNavigationState = {
        ...currentState,
        canGoPrevious: canGoPrevious
      };
      subject.next(newState);
    }
  }

  /**
   * Met à jour l'état du bouton "Enregistrer brouillon" pour une instance de wizard spécifique
   */
  updateCanSaveAsDraft(wizardInstanceId: string, canSaveAsDraft: boolean): void {
    this.updateNavigationState(wizardInstanceId, { canSaveAsDraft });
  }

  /**
   * Met à jour l'état du bouton "Terminer" pour une instance de wizard spécifique
   */
  updateCanComplete(wizardInstanceId: string, canComplete: boolean): void {
    const subject = this.wizardInstances.get(wizardInstanceId);
    if (subject) {
      const currentState = subject.value;
      const newState: IWizardNavigationState = {
        ...currentState,
        canComplete: canComplete
      };
      subject.next(newState);
    }
  }

  /**
   * Met à jour l'état de soumission de l'action "Terminer"
   * pour une instance de wizard spécifique.
   */
  updateCompleteActionInProgress(wizardInstanceId: string, inProgress: boolean): void {
    this.updateNavigationState(wizardInstanceId, { isCompleteActionInProgress: inProgress });
  }

  /**
   * Met à jour l'état de soumission de l'action "Suivant"
   * pour une instance de wizard spécifique.
   */
  updateNextActionInProgress(wizardInstanceId: string, inProgress: boolean): void {
    this.updateNavigationState(wizardInstanceId, { isNextActionInProgress: inProgress });
  }

  /**
   * Met à jour l'état du bouton "Ignorer" pour une instance de wizard spécifique
   */
  updateCanSkip(wizardInstanceId: string, canSkip: boolean): void {
    this.updateNavigationState(wizardInstanceId, { canSkip });
  }

  /**
   * Met à jour le message d'erreur pour une instance de wizard spécifique
   */
  updateErrorMessage(wizardInstanceId: string, errorMessageKey: string | undefined): void {
    this.updateNavigationState(wizardInstanceId, { errorMessageKey });
  }

  /**
   * Met à jour l'index de l'étape courante
   */
  setCurrentStep(wizardInstanceId: string, stepIndex: number): void {
    const currentState = this.getCurrentNavigationState(wizardInstanceId);
    if (currentState) {
      this.updateNavigationState(wizardInstanceId, {
        stepIndex: stepIndex,
        currentStepIndex: stepIndex,
        canGoPrevious: stepIndex > 0
      });
    }
  }

  /**
   * Marque une étape comme terminée
   */
  markStepAsCompleted(wizardInstanceId: string, stepIndex: number): void {
    const currentState = this.getCurrentNavigationState(wizardInstanceId);
    if (currentState && currentState.steps[stepIndex]) {
      const updatedSteps = [...currentState.steps];
      updatedSteps[stepIndex] = {
        ...updatedSteps[stepIndex],
        isCompleted: true
      };

      this.updateNavigationState(wizardInstanceId, { steps: updatedSteps });
    }
  }

  /**
   * Met à jour la configuration d'une étape spécifique
   */
  updateStepConfig(wizardInstanceId: string, stepIndex: number, stepConfig: Partial<IWizardStepConfig>): void {
    const currentState = this.getCurrentNavigationState(wizardInstanceId);
    if (currentState && currentState.steps[stepIndex]) {
      const updatedSteps = [...currentState.steps];
      updatedSteps[stepIndex] = {
        ...updatedSteps[stepIndex],
        ...stepConfig
      };

      this.updateNavigationState(wizardInstanceId, { steps: updatedSteps });
    }
  }

  /**
   * Vérifie si on peut aller à l'étape suivante
   */
  canGoToNextStep(wizardInstanceId: string): boolean {
    const currentState = this.getCurrentNavigationState(wizardInstanceId);
    return currentState ? currentState.canGoNext : false;
  }

  /**
   * Vérifie si on peut aller à l'étape précédente
   */
  canGoToPreviousStep(wizardInstanceId: string): boolean {
    const currentState = this.getCurrentNavigationState(wizardInstanceId);
    return currentState ? currentState.canGoPrevious : false;
  }

  /**
   * Vérifie si on peut ignorer l'étape courante
   */
  canSkipCurrentStep(wizardInstanceId: string): boolean {
    const currentState = this.getCurrentNavigationState(wizardInstanceId);
    return currentState ? currentState.canSkip : false;
  }

  /**
   * Vérifie si le wizard est terminé
   */
  isWizardCompleted(wizardInstanceId: string): boolean {
    const currentState = this.getCurrentNavigationState(wizardInstanceId);
    return currentState ? currentState.isCompleted : false;
  }

  /**
   * Marque le wizard comme terminé
   */
  markWizardAsCompleted(wizardInstanceId: string): void {
    this.updateNavigationState(wizardInstanceId, { isCompleted: true });
  }

  /**
   * Obtient la configuration d'une instance de wizard
   */
  getWizardConfig(wizardInstanceId: string): IWizardInstanceConfig | null {
    return this.wizardConfigs.get(wizardInstanceId) || null;
  }

  /**
   * Vérifie si une instance de wizard existe
   */
  hasWizardInstance(wizardInstanceId: string): boolean {
    return this.wizardInstances.has(wizardInstanceId);
  }

  /**
   * Obtient l'étape courante
   */
  getCurrentStep(wizardInstanceId: string): IWizardStepConfig | null {
    const currentState = this.getCurrentNavigationState(wizardInstanceId);
    if (currentState && currentState.steps[currentState.currentStepIndex]) {
      return currentState.steps[currentState.currentStepIndex];
    }
    return null;
  }

  /**
   * Obtient toutes les étapes
   */
  getAllSteps(wizardInstanceId: string): IWizardStepConfig[] {
    const currentState = this.getCurrentNavigationState(wizardInstanceId);
    return currentState ? currentState.steps : [];
  }

  /**
   * Vide toutes les instances de wizard (utile pour les tests ou le nettoyage global)
   */
  clearAllInstances(): void {
    this.wizardInstances.forEach((subject, wizardInstanceId) => {
      subject.complete();
    });
    this.wizardInstances.clear();
    this.wizardConfigs.clear();
  }
}
